import bin, {
  MaxValue,
  Schema,
  type AnySchema,
  type IMeasurer,
  type ISchema,
  type ISerialInput,
  type ISerialOutput,
} from "typed-binary";

export function u8concat(...arrays: Uint8Array[]) {
  let length = 0;
  for (const array of arrays) {
    length += array.byteLength;
  }

  const result = new Uint8Array(length);

  let offset = 0;
  for (const array of arrays) {
    result.set(array, offset);
    offset += array.byteLength;
  }

  return result;
}

export const u8equal = (a: Uint8Array, b: Uint8Array) => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

export class U64Schema extends Schema<bigint> {
  write(output: ISerialOutput, value: bigint): void {
    const b = new Uint8Array(8);

    // Ensure value is a proper BigInt
    const bigIntValue = typeof value === 'bigint' ? value : BigInt(0);
    
    new DataView(b.buffer, b.byteOffset, b.byteLength).setBigUint64(
      0,
      bigIntValue,
      output.endianness === "little"
    );

    output.writeSlice(b);
  }
  read(input: ISerialInput): bigint {
    const b = new Uint8Array(8);
    input.readSlice(b, 0, 8);
    return new DataView(b.buffer, b.byteOffset, b.byteLength).getBigUint64(
      0,
      input.endianness === "little"
    );
  }
  measure(
    _: bigint | MaxValue,
    measurer: IMeasurer = new bin.Measurer()
  ): IMeasurer {
    return measurer.add(8);
  }
}

declare module "typed-binary" {
  namespace bin {
    var u64: U64Schema;
  }
}

bin.u64 = new U64Schema();

export const binencode = <T extends AnySchema>(
  schema: T,
  object: bin.Parsed<NoInfer<T>>
) => {
  const measured = schema.measure(object);
  const buffer = new ArrayBuffer(measured.size);
  const writer = new bin.BufferWriter(buffer, { endianness: "big" });
  schema.write(writer, object);
  return new Uint8Array(buffer);
};

export const bindecode = <T>(
  schema: ISchema<T>,
  buffer: ArrayBufferLike | Uint8Array | null | undefined
): bin.Parsed<T> => {
  // Handle undefined or null buffer inputs
  if (!buffer) {
    throw new Error('Cannot decode undefined or null buffer');
  }
  
  let byteOffset = 0;
  let arrayBuffer: ArrayBufferLike;

  // Handle different buffer types
  if (buffer instanceof Uint8Array) {
    byteOffset = buffer.byteOffset;
    arrayBuffer = buffer.buffer;
  } else {
    arrayBuffer = buffer;
  }

  // Validate buffer has content
  if (!arrayBuffer || (arrayBuffer as ArrayBuffer).byteLength === 0) {
    throw new Error('Cannot decode empty buffer');
  }

  const reader = new bin.BufferReader(arrayBuffer, {
    endianness: "big",
    byteOffset,
  });
  
  try {
    return schema.read(reader);
  } catch (error: any) {
    console.error('Binary decode error:', error);
    throw new Error(`Failed to decode binary data: ${error?.message || 'Unknown error'}`);
  }
};
